import React, { useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FAQS, FAQ_CATEGORY_ORDER, FaqCategory } from "@/data/faqs";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useBooking } from "@/context/BookingContext";
import { trackEvent } from "@/utils/analytics";
import "./FaqPage.scss";

const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const { openBooking } = useBooking();
  const [expandedId, setExpandedId] = useState<string | null>(FAQS[0]?.id ?? null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    trackEvent("faq_view");
  }, []);

  useDocumentMeta({
    title: t("faq.pageTitle"),
    description: t("faq.pageDescription"),
    canonical: "https://devbloom.dev/faq",
    ogTitle: t("faq.pageTitle"),
    ogDescription: t("faq.pageDescription"),
    ogType: "website",
  });

  const toggle = useCallback(
    (id: string) => {
      setExpandedId((prev) => (prev === id ? null : id));
      trackEvent("faq_question_expand", { id });
    },
    []
  );

  const handleCta = useCallback(() => {
    trackEvent("faq_cta_click");
    openBooking("faq_page");
  }, [openBooking]);

  const faqsByCategory: Record<FaqCategory, typeof FAQS> = {
    studio: [],
    engagement: [],
    pricing: [],
    process: [],
    trust: [],
  };
  for (const item of FAQS) {
    faqsByCategory[item.category].push(item);
  }

  return (
    <main className="faq-page">
      <div className="container">
        <header className="faq-header">
          <p className="faq-label">{t("faq.heading")}</p>
          <h1 className="faq-heading">{t("faq.heading")}</h1>
          <p className="faq-sub">{t("faq.subheading")}</p>
        </header>

        <div className="faq-content">
          {FAQ_CATEGORY_ORDER.map((category) => {
            const items = faqsByCategory[category];
            if (!items.length) return null;
            return (
              <section key={category} className="faq-category">
                <h2 className="faq-category-title">
                  {t(`faq.categories.${category}`)}
                </h2>
                <dl className="faq-list">
                  {items.map((item) => {
                    const isExpanded = expandedId === item.id;
                    const panelId = `faq-panel-${item.id}`;
                    const buttonId = `faq-btn-${item.id}`;
                    return (
                      <div key={item.id} className={`faq-item${isExpanded ? " faq-item--open" : ""}`}>
                        <dt>
                          <button
                            id={buttonId}
                            aria-expanded={isExpanded}
                            aria-controls={panelId}
                            className="faq-question"
                            onClick={() => toggle(item.id)}
                          >
                            <span className="faq-question-text">
                              {t(item.questionKey)}
                            </span>
                            <span className="faq-chevron" aria-hidden="true">
                              {isExpanded ? "−" : "+"}
                            </span>
                          </button>
                        </dt>
                        <dd
                          id={panelId}
                          role="region"
                          aria-labelledby={buttonId}
                          className="faq-answer"
                          hidden={!isExpanded}
                        >
                          <p>{t(item.answerKey)}</p>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </section>
            );
          })}
        </div>

        <aside className="faq-cta-band">
          <p className="faq-cta-heading">{t("faq.cta")}</p>
          <p className="faq-cta-sub">{t("faq.ctaSubtext")}</p>
          <button className="faq-cta-btn" onClick={handleCta}>
            {t("faq.cta")}
          </button>
        </aside>
      </div>
    </main>
  );
};

export default FaqPage;
